import type {
  CognitoUser,
  CognitoUserAttribute,
  ISignUpResult,
} from "amazon-cognito-identity-js";
import { Auth } from "aws-amplify";

import { getCookie } from "~/v2/utils/cookies";
import { logoutAndRedirectToLogin } from "./authFailure";
import type { AuthRedirectReason } from "./MustReauthenticateError";
import {
  clearGrowSurfReferralCookie,
  parseSignupAttributionParams,
} from "./utm";

export type CognitoUserExtended = CognitoUser & {
  username?: string;
  // Amplify attaches user attributes on the session user object.
  attributes?: {
    email?: string;
    email_verified?: boolean | string;
    [attrName: string]: string | boolean | undefined;
  };
  challengeName?: string;
  challengeParam?: {
    CODE_DELIVERY_DESTINATION: string;
    userAttributes: {
      email: string;
      [attrName: string]: string;
    };
    codeParameter: string;
  };
};

const getSignUpSourceClientId = (): string | null => {
  const requestedPath = window?.sessionStorage?.getItem("requested_path");

  if (!requestedPath) {
    return null;
  }

  const searchParams = new URLSearchParams(
    requestedPath.substring(requestedPath.indexOf("?")),
  );
  return searchParams.get("client_id");
};

export const getAttributes = async (
  user: CognitoUser,
): Promise<CognitoUserAttribute[]> =>
  new Promise((resolve, reject) => {
    user.getUserAttributes((err, data) => {
      if (err || !data) {
        return reject(err);
      }
      resolve(data);
    });
  });

export const getCustomAttribute = (
  attributes: CognitoUserAttribute[],
  attr: string,
  defaultValue?: string,
): string => {
  for (const a of attributes) {
    if (a.Name === `custom:${attr}`) {
      return a.Value;
    }
    if (a.Name === attr) {
      return a.Value;
    }
  }

  if (defaultValue === undefined) {
    throw new Error(`cannot find custom attribute custom:${attr}`);
  }

  return defaultValue;
};

export const UserActions = {
  async getCurrentUser(): Promise<CognitoUser> {
    return await Auth.currentAuthenticatedUser();
  },
  async confirmMultiFactorAuth(
    user: CognitoUser,
    code: string,
    mfaType: "SMS_MFA" | "SOFTWARE_TOKEN_MFA" | null,
  ): Promise<CognitoUser> {
    return await Auth.confirmSignIn(user, code, mfaType);
  },
  async checkLoggedInStatus(): Promise<boolean> {
    try {
      const user = await this.getCurrentUser();
      return Boolean(user);
    } catch (e) {
      console.error(e);
      return false;
    }
  },
  verifyRecoveryCode: async (
    cognitoId: string,
    verifyId: string,
    userPool: string,
  ): Promise<void> => {
    // TODO: After recovery GET is only /owner/... in all envs, use that URL alone (remove legacy loop).
    const q = `?userpool=${userPool}`;
    for (const path of [
      `/accounts/${cognitoId}/account-recovery/${verifyId}${q}`,
      `/owner/account-recovery/${cognitoId}/${verifyId}${q}`,
    ]) {
      const response = await fetch(`${window.env.TRADING_API_BASE_URL}${path}`);
      if (response.ok) {
        return;
      }
      // Legacy path may 404 (no route) or 401 (e.g. ingress/auth) once /owner is canonical.
      if (response.status !== 404 && response.status !== 401) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          (errorData as { message?: string }).message ||
            "Invalid recovery code provided",
        );
      }
    }
    throw new Error("Invalid recovery code provided");
  },
  async registerUser({
    firstName,
    lastName,
    country,
    email,
    password,
    countryOfCitizenship,
    validationResponse,
    signupIPAddress,
    referralSource,
  }: {
    firstName: string;
    lastName: string;
    country: string;
    email: string;
    password: string;
    countryOfCitizenship: string;
    validationResponse: string;
    signupIPAddress: string;
    referralSource: string;
  }): Promise<ISignUpResult> {
    const params = new URLSearchParams(window.location.search);
    const utmParams = parseSignupAttributionParams(params);
    const clientId = getSignUpSourceClientId();

    const result = await Auth.signUp({
      username: email,
      password,
      attributes: {
        "custom:firstName": firstName,
        "custom:lastName": lastName,
        "custom:country": country,
        "custom:citizenship": countryOfCitizenship,
        "custom:onboardingVersion": "v2",
        "custom:accountCreated": "no",
        "custom:crypto_broker": params.has("xcrypto") ? "BS" : undefined,
        "custom:validationResponse": validationResponse,
        "custom:signupIPAddress": signupIPAddress,
        ...(Object.keys(utmParams).length && {
          "custom:utm_params": JSON.stringify(utmParams),
        }),
        ...(clientId && {
          "custom:signup_source": clientId,
        }),
        // Cognito is very restrictive about what text it will accept
        "custom:referral_source": referralSource
          .toLowerCase()
          .replace(" ", "_")
          .replace(/[^a-z0-9_]/g, ""),
      },
    });
    clearGrowSurfReferralCookie();
    return result;
  },
  userLogin(email: string, password: string): Promise<CognitoUserExtended> {
    return Auth.signIn(email, password);
  },
  async userLogout(
    opts?: { global?: boolean },
    redirectReason: AuthRedirectReason = "manual_logout",
  ): Promise<void> {
    await logoutAndRedirectToLogin({ redirectReason, signOutOptions: opts });
  },
  async confirmUserSignUp(email: string, code: string): Promise<void> {
    await Auth.confirmSignUp(email, code, {
      forceAliasCreation: true,
    });
  },
  async resendUserSignUp(username: string) {
    await Auth.resendSignUp(username);
  },
  async rememberDevice() {
    try {
      await Auth.rememberDevice();
    } catch (error) {
      console.error("Error remembering device", error);
    }
  },
  async forgetDevice() {
    try {
      await Auth.forgetDevice();
    } catch (error) {
      console.error("Error forgetting device", error);
    }
  },
  async getSavedDevices() {
    try {
      return await Auth.fetchDevices();
    } catch (error) {
      console.error("Error getting saved devices", error);
      return [];
    }
  },
  async isDeviceRemembered() {
    try {
      const devices = await Auth.fetchDevices();
      const currentUser =
        (await Auth.currentAuthenticatedUser()) as CognitoUser;
      // @ts-expect-error: keyPrefix exists but is not typed...
      const keyPrefix = currentUser.keyPrefix;
      const username = currentUser.getUsername();
      const key = `${keyPrefix}.${username}.deviceKey`;
      const value = getCookie(key);

      const isDeviceRemembered = devices.some((device) => device.id === value);

      return isDeviceRemembered;
    } catch (error) {
      console.error("Error checking if device is remembered", error);
      return false;
    }
  },
  async forgetAllDevices() {
    try {
      const currentUser =
        (await Auth.currentAuthenticatedUser()) as CognitoUser;
      const devices = await Auth.fetchDevices();

      await Promise.all(
        devices.map(
          (device) =>
            new Promise((resolve, reject) => {
              currentUser.forgetSpecificDevice(device.id, {
                onSuccess: () => resolve(null),
                onFailure: (error) => reject(error),
              });
            }),
        ),
      );
    } catch (error) {
      console.error("Error forgetting specific device", error);
    }
  },
};
